import { useCallback, useEffect, useState } from 'react';

/**
 * Web push subscription state machine.
 *
 * Three concerns rolled into one hook so settings screens can
 * present a single toggle:
 *
 *   1. Capability — does the browser support service workers + Push?
 *   2. Permission — has the user granted Notification permission?
 *   3. Subscription — do we have a `PushSubscription` registered with
 *      the browser's push service, ready to be POSTed to the server?
 *
 * When `subscribe()` resolves, the caller POSTs
 * `JSON.stringify(subscription.toJSON())` to `/users/me/push-tokens`
 * with `platform: 'web'` (see NotificationsScreen).
 */
export interface PushState {
  supported: boolean;
  permission: NotificationPermission | 'unsupported';
  subscription: PushSubscription | null;
  busy: boolean;
  error: string | null;
}

export interface UsePushSubscriptionResult extends PushState {
  /**
   * Request permission, register the service worker (if needed) and
   * subscribe to push. Returns the resolved subscription on success
   * (caller decides what to do with it) or null on cancel / failure.
   */
  subscribe: (vapidPublicKey?: string) => Promise<PushSubscription | null>;
  /** Best-effort unsubscribe; resolves true when the subscription is gone. */
  unsubscribe: () => Promise<boolean>;
}

function detectSupported(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const rawData = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) out[i] = rawData.charCodeAt(i);
  return out;
}

export function usePushSubscription(): UsePushSubscriptionResult {
  const supported = detectSupported();
  const [state, setState] = useState<PushState>(() => ({
    supported,
    permission: supported ? Notification.permission : 'unsupported',
    subscription: null,
    busy: false,
    error: null,
  }));

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    void (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg) return;
        const sub = await reg.pushManager.getSubscription();
        if (cancelled) return;
        setState((prev) => ({ ...prev, subscription: sub }));
      } catch (err) {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          error: err instanceof Error ? err.message : 'Could not read push subscription.',
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [supported]);

  const subscribe = useCallback(
    async (vapidPublicKey?: string): Promise<PushSubscription | null> => {
      if (!supported) {
        setState((prev) => ({
          ...prev,
          error: 'Push notifications are not supported in this browser.',
        }));
        return null;
      }
      setState((prev) => ({ ...prev, busy: true, error: null }));
      try {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          setState((prev) => ({ ...prev, permission, busy: false }));
          return null;
        }
        const reg =
          (await navigator.serviceWorker.getRegistration()) ??
          (await navigator.serviceWorker.register('/sw.js', { scope: '/' }));
        await navigator.serviceWorker.ready;

        const existing = await reg.pushManager.getSubscription();
        if (existing) {
          setState((prev) => ({
            ...prev,
            subscription: existing,
            permission,
            busy: false,
          }));
          return existing;
        }

        if (!vapidPublicKey) {
          setState((prev) => ({
            ...prev,
            permission,
            busy: false,
            error:
              'Enabled in this browser, but the server has not published a VAPID key yet. Pushes will start once the deploy includes one.',
          }));
          return null;
        }

        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey).buffer as ArrayBuffer,
        });
        setState((prev) => ({
          ...prev,
          subscription: sub,
          permission,
          busy: false,
        }));
        return sub;
      } catch (err) {
        setState((prev) => ({
          ...prev,
          busy: false,
          error: err instanceof Error ? err.message : 'Subscribe failed.',
        }));
        return null;
      }
    },
    [supported],
  );

  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!supported) return false;
    setState((prev) => ({ ...prev, busy: true, error: null }));
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      if (!sub) {
        setState((prev) => ({ ...prev, subscription: null, busy: false }));
        return true;
      }
      const ok = await sub.unsubscribe();
      setState((prev) => ({ ...prev, subscription: null, busy: false }));
      return ok;
    } catch (err) {
      setState((prev) => ({
        ...prev,
        busy: false,
        error: err instanceof Error ? err.message : 'Unsubscribe failed.',
      }));
      return false;
    }
  }, [supported]);

  return { ...state, subscribe, unsubscribe };
}
